import { Point } from '@printu/geom';
import { Line } from './Line';
import { CURVETIME_EPSILON, EPSILON, GEOMETRIC_EPSILON, solveCubic, } from './numerical';
import { createSegment } from './Segment';
import { getminmax, numberSort } from './utils';
export function isStraight(curve) {
    const [p1, h1, h2, p2] = curve;
    if (h1.isZero() && h2.isZero()) {
        // No handles.
        return true;
    }
    const vec = p2.subtract(p1);
    if (vec.isZero()) {
        // Zero-length line, with some handles defined.
        return false;
    }
    if (vec.isCollinear(h1) && vec.isCollinear(h2)) {
        // Collinear handles: In addition to v.isCollinear(h) checks, we
        // need to measure the distance to the line, in order to be able
        // to use the same epsilon as in Curve#getTimeOf(), see #1066.
        const line = new Line(p1, p2);
        const epsilon = GEOMETRIC_EPSILON;
        if (line.getDistance(p1.add(h1)) < epsilon &&
            line.getDistance(p2.add(h2)) < epsilon) {
            // Project handles onto line to see if they are in range:
            const div = vec.dot(vec);
            const s1 = vec.dot(h1) / div;
            const s2 = vec.dot(h2) / div;
            return s1 >= 0 && s1 <= 1 && s2 <= 0 && s2 >= -1;
        }
    }
    return false;
}
export function isLinear(curve) {
    const [p1, h1, h2, p2] = curve;
    const third = p2.subtract(p1).scale(1 / 3);
    return h1.equals(third) && h2.clone().negate().equals(third);
}
export function createSegments(curve) {
    const control1 = curve[1].subtract(curve[0]);
    const control2 = curve[2].subtract(curve[3]);
    return [
        createSegment(curve[0], undefined, control1),
        createSegment(curve[3], control2, undefined),
    ];
}
export const createCubicBezier = (start, end) => {
    const p0 = start.point;
    const p3 = end.point;
    return [p0, p0.add(start.handleOut), p3.add(end.handleIn), p3];
};
export class CubicBezier {
    constructor(param0, param1) {
        Object.defineProperty(this, "cubic", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "derivatives", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        if (Array.isArray(param0)) {
            this.cubic = param0.concat();
        }
        else {
            this.cubic = createCubicBezier(param0, param1);
        }
        this.derivatives = this.derive();
    }
    get(t) {
        return this.compute(t);
    }
    compute(t) {
        // shortcuts
        if (t === 0) {
            return this.cubic[0];
        }
        const order = this.cubic.length - 1;
        if (t === 1) {
            return this.cubic[order];
        }
        const points = this.cubic;
        const mt = 1 - t;
        const mt2 = mt * mt;
        const t2 = t * t;
        // cubic coefs
        const aCoef = mt2 * mt;
        const bCoef = mt2 * t * 3;
        const cCoef = mt * t2 * 3;
        const dCoef = t * t2;
        return new Point(
        /* x */
        aCoef * points[0].x +
            bCoef * points[1].x +
            cCoef * points[2].x +
            dCoef * points[3].x, 
        /* y */
        aCoef * points[0].y +
            bCoef * points[1].y +
            cCoef * points[2].y +
            dCoef * points[3].y);
    }
    /**
     * Tight curve bbox
     * @see https://pomax.github.io/bezierinfo/#tightbounds
     */
    tightbbox() {
        const { atan2, sin, cos } = Math;
        // 1. translate to first point
        // 2. rotate so that last point is (...,0)
        const anchor = this.cubic[0];
        const tx = anchor.x;
        const ty = anchor.y;
        const lastPoint = this.cubic[this.cubic.length - 1];
        const angle = atan2(lastPoint.y - ty, lastPoint.x - tx);
        const alignedCubic = this.cubic.map(point => {
            const relX = point.x - tx;
            const relY = point.y - ty;
            return new Point(relX * cos(-angle) - relY * sin(-angle), relX * sin(-angle) + relY * cos(-angle));
        });
        // create rotated curve
        const rtcurve = new CubicBezier(...createSegments(alignedCubic));
        const extrema = rtcurve.extrema();
        // and the we run the regular bounding box code
        let minx = Number.MAX_SAFE_INTEGER;
        let miny = minx;
        let maxx = Number.MIN_SAFE_INTEGER;
        let maxy = maxx;
        [0, ...extrema.x, ...extrema.y, 1].forEach(t => {
            const rtp = rtcurve.get(t);
            if (rtp.x < minx) {
                minx = rtp.x;
            }
            if (rtp.x > maxx) {
                maxx = rtp.x;
            }
            if (rtp.y < miny) {
                miny = rtp.y;
            }
            if (rtp.y > maxy) {
                maxy = rtp.y;
            }
        });
        const ca = cos(angle);
        const sa = sin(angle);
        return [
            new Point(tx + minx * ca - miny * sa, ty + minx * sa + miny * ca),
            new Point(tx + maxx * ca - miny * sa, ty + maxx * sa + miny * ca),
            new Point(tx + maxx * ca - maxy * sa, ty + maxx * sa + maxy * ca),
            new Point(tx + minx * ca - maxy * sa, ty + minx * sa + maxy * ca),
        ];
    }
    /**
     * Curve bbox
     * @see https://pomax.github.io/bezierinfo/#boundingbox
     */
    bbox() {
        const extrema = this.extrema();
        const result = {
            x: getminmax(this, 'x', extrema.x),
            y: getminmax(this, 'y', extrema.y),
        };
        return result;
    }
    /**
     * Finds curve extrema points
     */
    extrema() {
        const dims = ['x', 'y'];
        const result = {
            x: [],
            y: [],
            values: [],
        };
        let roots = [];
        dims.forEach(dim => {
            result[dim] = this.derivativeRoots(0, dim);
            result[dim] = result[dim].concat(this.derivativeRoots(1, dim));
            result[dim] = result[dim].filter(t => t >= 0 && t <= 1);
            roots = roots.concat(result[dim].sort(numberSort));
        });
        roots = roots
            .sort(numberSort)
            .filter((root, idx) => roots.indexOf(root) === idx);
        result.values = roots;
        return result;
    }
    /**
     * Cubic derivative 2nd ans 1st order
     */
    derive() {
        const dpoints = [];
        let points = this.cubic;
        for (let idx = points.length, prevIdx = idx - 1; idx > 1; idx -= 1, prevIdx -= 1) {
            const list = [];
            for (let j = 0, dpt; j < prevIdx; j += 1) {
                dpt = {
                    x: prevIdx * (points[j + 1].x - points[j].x),
                    y: prevIdx * (points[j + 1].y - points[j].y),
                };
                list.push(dpt);
            }
            dpoints.push(list);
            points = list;
        }
        return dpoints;
    }
    /**
     * Derivative roots
     * @param points
     * @param dim
     */
    derivativeRoots(degree, dim) {
        // quadratic roots are easy
        const { sqrt } = Math;
        const points = this.derivatives[degree];
        if (points.length === 3) {
            const aCoef = points[0][dim];
            const bCoef = points[1][dim];
            const cCoef = points[2][dim];
            const dCoef = aCoef - 2 * bCoef + cCoef;
            if (dCoef !== 0) {
                const m1 = -sqrt(bCoef * bCoef - aCoef * cCoef);
                const m2 = -aCoef + bCoef;
                const v1 = -(m1 + m2) / dCoef;
                const v2 = -(-m1 + m2) / dCoef;
                return [v1, v2];
            }
            if (bCoef !== cCoef && dCoef === 0) {
                return [(2 * bCoef - cCoef) / (2 * (bCoef - cCoef))];
            }
            return [];
        }
        // linear roots are even easier
        if (points.length === 2) {
            const aCoef = points[0][dim];
            const bCoef = points[1][dim];
            if (aCoef !== bCoef) {
                return [aCoef / (aCoef - bCoef)];
            }
            return [];
        }
        return [];
    }
    /**
     * Get nearest on curve location
     */
    getNearestLocation(point) {
        const t = this.getNearestTime(point);
        const pt = this.get(t);
        return {
            t,
            point: pt,
            distance: point.getDistance(pt),
        };
    }
    getNearestTime(point) {
        const { cubic } = this;
        if (isStraight(cubic)) {
            const [p1, , , p2] = cubic;
            const x0 = p1.x;
            const y0 = p1.y;
            const x3 = p2.y;
            const y3 = p2.y;
            const vx = x3 - x0;
            const vy = y3 - y0;
            const det = vx * vx + vy * vy;
            // Avoid divisions by zero.
            if (det === 0) {
                return 0;
            }
            // Project the point onto the line and calculate its linear
            // parameter u along the line: u = (point - p1).dot(v) / v.dot(v)
            const uParam = ((point.x - x0) * vx + (point.y - y0) * vy) / det;
            if (uParam < EPSILON) {
                return 0;
            }
            if (uParam > 1 - EPSILON) {
                return 1;
            }
            return this.getTimeOf(new Point(x0 + uParam * vx, y0 + uParam * vy));
            // return uParam < EPSILON
            //     ? 0
            //     : uParam > 1 - EPSILON
            //     ? 1
            //     : this.getTimeOf(new Point(x0 + uParam * vx, y0 + uParam * vy));
        }
        const count = 100;
        let minDist = Infinity;
        let minT = 0;
        const refine = (t) => {
            if (t >= 0 && t <= 1) {
                const dist = point.getDistance(this.get(t), true);
                if (dist < minDist) {
                    minDist = dist;
                    minT = t;
                    return true;
                }
            }
            return false;
        };
        for (let i = 0; i <= count; i += 1) {
            refine(i / count);
        }
        // Now iteratively refine solution until we reach desired precision.
        let step = 1 / (count * 2);
        while (step > CURVETIME_EPSILON) {
            if (!refine(minT - step) && !refine(minT + step)) {
                step /= 2;
            }
        }
        return minT;
    }
    getTimeOf(point) {
        const { cubic } = this;
        const [p0, , , p3] = cubic;
        // Before solving cubics, compare the beginning and end of the curve
        // with zero epsilon:
        const epsilon = EPSILON;
        const geomEpsilon = GEOMETRIC_EPSILON;
        if (point.isClose(p0, epsilon)) {
            return 0;
        }
        if (point.isClose(p3, epsilon)) {
            return 1;
        }
        // const t = point.isClose(p0, epsilon)
        //     ? 0
        //     : point.isClose(p3, epsilon)
        //     ? 1
        //     : null;
        let t = null;
        // Solve the cubic for both x- and y-coordinates and consider all
        // solutions, testing with the larger / looser geometric epsilon.
        const roots = [];
        // c is 0 - x, 1 - y
        ['x', 'y'].forEach(coord => {
            const count = this.solveCubic(coord, point, roots);
            for (let i = 0; i < count; i += 1) {
                const root = roots[i];
                if (point.isClose(this.get(root), geomEpsilon)) {
                    t = root;
                }
            }
        });
        // Since we're comparing with geometric epsilon for any other t along
        // the curve, do so as well now for the beginning and end of the curve.
        if (point.isClose(p0, geomEpsilon)) {
            return 0;
        }
        if (point.isClose(p3, geomEpsilon)) {
            return 1;
        }
        return t;
        // return point.isClose(p0, geomEpsilon)
        //     ? 0
        //     : point.isClose(p3, geomEpsilon)
        //     ? 1
        //     : null;
    }
    solveCubic(coord, point, roots, min = 0, max = 1) {
        const [p1, c1, c2, p2] = this.cubic;
        const v0 = p1[coord];
        const v1 = c1[coord];
        const v2 = c2[coord];
        const v3 = p2[coord];
        const val = point[coord];
        let res = 0;
        // If val is outside the curve values, no solution is possible.
        if (!((v0 < val && v3 < val && v1 < val && v2 < val) ||
            (v0 > val && v3 > val && v1 > val && v2 > val))) {
            const cCoef = 3 * (v1 - v0);
            const bCoef = 3 * (v2 - v1) - cCoef;
            const aCoef = v3 - v0 - cCoef - bCoef;
            res = solveCubic(aCoef, bCoef, cCoef, v0 - val, roots, min, max);
        }
        return res;
    }
}
